import { Box, Paper, Typography } from "@mui/material";
import React from "react";

// Helper function to get player details from allPlayers array
const getPlayerDetails = (playerId: string, allPlayers: any[]) => {
  if (!allPlayers || !playerId) return null;
  const player = allPlayers.find((p) => p._id === playerId);
  return player || null;
};

// Helper function to format event type for display
const formatEventType = (type: string) => {
  return type
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

interface MatchEventsTimelineProps {
  slots: any[];
  allPlayers: any[];
}

const MatchEventsTimeline: React.FC<MatchEventsTimelineProps> = ({
  slots,
  allPlayers,
}) => {
  if (!slots || slots.length === 0) {
    return null;
  }

  return (
    <Paper elevation={4} style={{ margin: 14, padding: 14 }}>
      <Typography variant="h6" fontWeight={"bold"} gutterBottom>
        Match Events Timeline
      </Typography>
      <Box style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {slots.map((slot, slotIndex) => (
          <Paper
            key={slotIndex}
            elevation={2}
            sx={{ p: 3, backgroundColor: "#fafafa" }}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <Box
                style={{
                  display: "flex",
                  gap: 16,
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={"bold"}
                  color="primary"
                >
                  {slot.time}' - Half {slot.matchHalf}
                </Typography>
                <Typography variant="body2" fontWeight={"bold"}>
                  Score: {slot.myTeamScore} - {slot.oppTeamScore}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: "#e3f2fd",
                  padding: "4px 8px",
                  borderRadius: "4px",
                }}
              >
                {formatEventType(slot.playCategory)}
              </Typography>
            </Box>

            {/* Events in this slot */}
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {slot.events.map((event, eventIndex) => {
                const playerDetails = event.players
                  ?.map((playerId) => getPlayerDetails(playerId, allPlayers))
                  .filter((p) => p !== null);

                return (
                  <Box
                    key={eventIndex}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      padding: "10px 12px",
                      backgroundColor:
                        event.type === "GOAL" ? "#c8e6c9" : "#ffffff",
                      borderRadius: "4px",
                      borderLeft: `4px solid ${
                        event.team === "A" ? "#1976d2" : "#d32f2f"
                      }`,
                    }}
                  >
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          minWidth: 60,
                          fontWeight: "bold",
                          color: event.team === "A" ? "#1976d2" : "#d32f2f",
                        }}
                      >
                        Team {event.team}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ flex: 1, fontWeight: "500" }}
                      >
                        {playerDetails?.map((p) => p.name).join(", ") ||
                          "Unknown Player"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          minWidth: 120,
                          fontWeight: event.type === "GOAL" ? "bold" : "normal",
                          color:
                            event.type === "GOAL"
                              ? "#2e7d32"
                              : "text.secondary",
                          textAlign: "right",
                        }}
                      >
                        {formatEventType(event.type)}
                      </Typography>
                    </Box>
                    {playerDetails && playerDetails.length > 0 && (
                      <Box style={{ paddingLeft: 72 }}>
                        {playerDetails.map((player, idx) => (
                          <Box key={idx} style={{ marginTop: idx > 0 ? 4 : 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Player ID:{" "}
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  fontWeight: "bold",
                                  color: "#1976d2",
                                  fontFamily: "monospace",
                                }}
                              >
                                {player.playerId}
                              </Typography>
                              {" | "}
                              Player Card ID:{" "}
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  fontWeight: "bold",
                                  color: "#1976d2",
                                  fontFamily: "monospace",
                                }}
                              >
                                {player.playerCardId}
                              </Typography>
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
};

export default MatchEventsTimeline;
